import React from 'react';
import { Header, Icon, Form, Button, Message, Segment, Label, Popup, Input } from 'semantic-ui-react';
import type { ClientResponse, ClientListResponse } from '../../types/dtos';
import { ClientObservationEntryDto } from '../../services/contracts/ContractService';
import ClientObservationsSummary from '../clients/ClientObservationsSummary';
import { daysSinceLastCnpjSync, CNPJ_SYNC_STALE_DAYS, isMeiOuIndividual } from '../../utils/iePolicy';
import { CNPJ_SEARCH_TEXT_ERROR } from '../../hooks/contracts/useClientCnpjSearch';
import {
  getClientClassificationMeta,
  readClientClassification,
  readClientInternalNotes,
} from '../../utils/clientClassification';

export interface ContractFormClienteTabProps {
  editingContractId: string | null;
  selectedClient: ClientResponse | null | undefined;
  clientObservations?: ClientObservationEntryDto[];
  clientObservationsLoading?: boolean;
  onClearClient: () => void;
  clientSearchQuery: string;
  setClientSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  loadClients: (query?: string) => void;
  clientsLoading: boolean;
  searchingCnpj?: boolean;
  onSearchCnpj: () => void;
  cnpjInputError: string;
  onClientSearchBlur: () => void;
  error: string;
  clients: ClientListResponse[];
  onSelectClient: (client: ClientListResponse) => void;
}

export const ContractFormClienteTab: React.FC<ContractFormClienteTabProps> = ({
  editingContractId,
  selectedClient,
  clientObservations = [],
  clientObservationsLoading = false,
  onClearClient,
  clientSearchQuery,
  setClientSearchQuery,
  loadClients,
  clientsLoading,
  searchingCnpj = false,
  onSearchCnpj,
  cnpjInputError,
  onClientSearchBlur,
  error,
  clients,
  onSelectClient,
}) => {
  const selectedClassification = getClientClassificationMeta(readClientClassification(selectedClient));
  const legacyClientNotes = readClientInternalNotes(selectedClient);
  const hasStructuredObservations = clientObservations.length > 0;
  const showLegacyNotes = !hasStructuredObservations && !!legacyClientNotes?.trim();

  return (
    <div style={{ padding: '20px' }}>
      <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="user" />
        {editingContractId ? 'Cliente do Contrato' : 'Seleção do Cliente'}
      </Header>

      <p style={{ color: '#666', marginBottom: '20px' }}>
        {editingContractId
          ? 'Em modo de edição o cliente não pode ser alterado.'
          : 'Selecione o cliente para o qual será criado o contrato. Você pode buscar por razão social, CNPJ ou nome fantasia.'}
      </p>

      {selectedClient && (
        <Segment
          style={{
            backgroundColor: editingContractId ? '#f0f0f0' : '#e8f5e8',
            border: `2px solid ${editingContractId ? '#999' : '#4caf50'}`,
            marginBottom: '20px',
          }}
        >
          <Label color="green" ribbon>
            <Icon name="check" />
            Cliente Selecionado
          </Label>
          <div style={{ marginTop: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px', flexWrap: 'wrap' }}>
              <Header as="h4" style={{ margin: '0' }}>
                {selectedClient.razaoSocial}
              </Header>
              <Label color={selectedClient.situacaoCadastral === 'ATIVA' ? 'green' : 'red'} size="small">
                {selectedClient.situacaoCadastral === 'ATIVA' ? 'Ativa' : 'Inativo'}
              </Label>
              <Label color={selectedClassification.color} size="small">
                <Icon name="tag" />
                {selectedClassification.label}
              </Label>
              {selectedClient.situacaoCadastral !== 'ATIVA' && (
                <Popup
                  content="Situação cadastral diferente de ATIVA na Receita. Contratos Fastchip podem ser criados — marque Fastchip na aba Contrato. Contratos por entrega exigem cliente ATIVO."
                  trigger={
                    <Label color="orange" size="small" style={{ cursor: 'default' }}>
                      <Icon name="info circle" />
                      Cadastro inativo
                    </Label>
                  }
                />
              )}
              {/* Badge IE INAPTA — só se não-MEI e snapshot confirmar inaptidão */}
              {!isMeiOuIndividual(selectedClient) &&
                selectedClient.hasActiveInscricaoEstadual === false && (
                  <Popup
                    content="Este cliente não possui Inscrição Estadual ativa no UF. Contratos Fastchip podem ser criados normalmente. O bloqueio de IE inapta aplica-se apenas a contratos por entrega (sem Fastchip) com linhas Aditivo."
                    trigger={
                      <Label color="red" size="small" style={{ cursor: 'default' }}>
                        <Icon name="warning sign" />
                        IE INAPTA
                      </Label>
                    }
                  />
                )}
            </div>
            <p style={{ margin: '0', color: '#666' }}>
              <strong>CNPJ:</strong> {selectedClient.cnpj}
            </p>
            {selectedClient.nomeFantasia && (
              <p style={{ margin: '0', color: '#666' }}>
                <strong>Nome Fantasia:</strong> {selectedClient.nomeFantasia}
              </p>
            )}
            {(selectedClient.cidade || selectedClient.estado) && (
              <p style={{ margin: '0', color: '#666' }}>
                <strong>Localização:</strong>{' '}
                {[selectedClient.cidade, selectedClient.estado].filter(Boolean).join(' — ')}
              </p>
            )}
            {(hasStructuredObservations || clientObservationsLoading || showLegacyNotes) && (
              <Message
                info
                size="small"
                style={{ marginTop: '8px', marginBottom: '0', borderLeft: '4px solid #2185d0' }}
              >
                <Message.Header>Observações internas do cliente</Message.Header>
                {hasStructuredObservations || clientObservationsLoading ? (
                  <div style={{ marginTop: 8 }}>
                    <ClientObservationsSummary
                      observations={clientObservations}
                      loading={clientObservationsLoading}
                      maxRows={4}
                      maxHeight={220}
                      dense
                    />
                  </div>
                ) : (
                  <p style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{legacyClientNotes}</p>
                )}
              </Message>
            )}
            {/* Indicador de última sincronização com a Receita */}
            {(() => {
              // dataUltimaConsultaCnpj vem como string JSON antes de qualquer conversão para Date.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const rawSync = (selectedClient as any).dataUltimaConsultaCnpj;
              const syncField = rawSync != null ? String(rawSync) : undefined;
              if (!syncField) return null;
              const days = daysSinceLastCnpjSync(syncField);
              const stale = days > CNPJ_SYNC_STALE_DAYS;
              return (
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: stale ? '#856404' : '#666' }}>
                  <Icon name={stale ? 'clock' : 'sync'} />
                  Última consulta Receita:{' '}
                  {new Date(syncField).toLocaleDateString('pt-BR')}
                  {stale && (
                    <span style={{ marginLeft: '6px', fontWeight: 'bold' }}>
                      — dados podem estar desatualizados
                    </span>
                  )}
                </p>
              );
            })()}
            {!editingContractId && (
              <Button size="small" color="red" onClick={onClearClient} style={{ marginTop: '10px' }}>
                <Icon name="trash" />
                Limpar Seleção
              </Button>
            )}
          </div>
        </Segment>
      )}

      {!editingContractId && (
        <>
          <Form onSubmit={(event) => event.preventDefault()}>
            <Form.Field error={!!cnpjInputError}>
              <label htmlFor="contract-client-search">Buscar Cliente</label>
              <Input
                id="contract-client-search"
                fluid
                error={!!cnpjInputError}
                type="text"
                placeholder="Razão social, nome fantasia ou CNPJ (14 dígitos para consultar)..."
                value={clientSearchQuery}
                onChange={(e) => setClientSearchQuery(e.target.value)}
                onBlur={onClientSearchBlur}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  if (!event.repeat && !event.nativeEvent.isComposing) onClientSearchBlur();
                }}
                aria-invalid={!!cnpjInputError}
                aria-describedby={cnpjInputError ? 'contract-client-search-help contract-client-search-error' : 'contract-client-search-help'}
              />
              <div style={{ display: 'grid', color: '#9f3a38', marginTop: '5px', fontSize: '0.9em' }}>
                {/* Reserva a altura máxima também quando o erro está oculto, preservando o alvo do clique no blur. */}
                <span aria-hidden="true" style={{ gridArea: '1 / 1', visibility: 'hidden' }}>{CNPJ_SEARCH_TEXT_ERROR}</span>
                {cnpjInputError && (
                  <div id="contract-client-search-error" role="alert" style={{ gridArea: '1 / 1' }}>
                    {cnpjInputError}
                  </div>
                )}
              </div>
              <div id="contract-client-search-help" style={{ color: '#666', marginTop: '5px', marginBottom: '10px', fontSize: '0.9em' }}>
                Busque por razão social, nome fantasia ou CNPJ nos clientes cadastrados. Consultar CNPJ aceita apenas CNPJ com 14 dígitos.
              </div>
            </Form.Field>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <Button
                type="button"
                primary
                onClick={() => loadClients(clientSearchQuery.trim() || undefined)}
                loading={clientsLoading}
                disabled={clientsLoading}
              >
                <Icon name="search" />
                {clientsLoading ? 'Carregando...' : 'Buscar Clientes Cadastrados'}
              </Button>

              <Button
                type="button"
                secondary
                onClick={onSearchCnpj}
                loading={searchingCnpj}
                disabled={searchingCnpj || !clientSearchQuery.trim()}
              >
                <Icon name="external alternate" />
                {searchingCnpj ? 'Consultando...' : 'Consultar CNPJ'}
              </Button>
            </div>
          </Form>

          {error && (
            <Message error style={{ marginBottom: '20px' }}>
              <Message.Header>Erro</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {clients.length > 0 && (
            <div>
              <Header as="h4" style={{ color: '#2c3e50' }}>
                Clientes Encontrados ({clients.length})
              </Header>

              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                }}
              >
                {clients.map((client) => {
                  const classification = getClientClassificationMeta(readClientClassification(client));
                  return (
                  <div
                    key={client.id}
                    style={{
                      padding: '15px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: selectedClient?.id === client.id ? '#f0f8ff' : 'white',
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => onSelectClient(client)}
                    onMouseEnter={(e) => {
                      if (selectedClient?.id !== client.id) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedClient?.id !== client.id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <strong>{client.razaoSocial}</strong>
                          <Label color={client.situacaoCadastral === 'ATIVA' ? 'green' : 'red'} size="tiny">
                            {client.situacaoCadastral === 'ATIVA' ? 'Ativa' : 'Inativo'}
                          </Label>
                          <Label color={classification.color} size="tiny">
                            <Icon name="tag" />
                            {classification.label}
                          </Label>
                        </div>
                        <br />
                        <small style={{ color: '#666' }}>
                          CNPJ: {client.cnpj}
                          {client.nomeFantasia && ` • ${client.nomeFantasia}`}
                        </small>
                      </div>
                      <Icon
                        name={selectedClient?.id === client.id ? 'check circle' : 'circle outline'}
                        color={selectedClient?.id === client.id ? 'green' : 'grey'}
                        size="large"
                      />
                    </div>
                  </div>
                )})}
              </div>
            </div>
          )}

          {!clientsLoading && clients.length === 0 && (
            <Message info>
              <Message.Header>Nenhum cliente encontrado</Message.Header>
              <p>Clique em "Buscar Clientes" para carregar a lista de clientes disponíveis.</p>
            </Message>
          )}

          {!clientsLoading && clients.length === 0 && clientSearchQuery && (
            <Message warning>
              <Message.Header>Nenhum cliente encontrado</Message.Header>
              <p>Não foram encontrados clientes que correspondam à sua busca: "{clientSearchQuery}"</p>
            </Message>
          )}
        </>
      )}
    </div>
  );
};
